import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react'
import { useTranslation } from 'react-i18next'
import {
  uiLocaleToLanguageCode,
  languageCodeToUiLocale,
  type UiLocale,
} from '../../core/contracts.js'
import { clientSessionViewStore } from '../../../services/clientSessionViewStore.js'
import { initI18n } from '../../i18n/index.js'
import { isUiLocaleExplicit, resolveUiLocale } from '../../i18n/resolve-ui-locale.js'
import { persistSessionUiLocale } from '../../lib/persist-session-locale.js'

export interface UseLocaleResult {
  locale: UiLocale
  setLocale: (locale: UiLocale, options?: { explicit?: boolean }) => void
  promoteLocaleFromAgent: (languageCode: string) => void
  languageCode: string
  t: ReturnType<typeof useTranslation>['t']
  isExplicit: boolean
}

function readLocaleSnapshot(): UiLocale {
  const snap = clientSessionViewStore.getSnapshot()
  return resolveUiLocale({
    uiLocale: snap.uiLocale,
    uiLocaleExplicit: snap.uiLocaleExplicit,
    language: snap.language,
    livePolicyLanguageCode: snap.livePolicy?.languageCode,
  })
}

function readExplicitSnapshot(): boolean {
  return isUiLocaleExplicit(clientSessionViewStore.getSnapshot())
}

export function useLocale(): UseLocaleResult {
  const initialLocale = readLocaleSnapshot()
  initI18n(initialLocale)

  const subscribe = useCallback((onStoreChange: () => void) => {
    if (typeof clientSessionViewStore.subscribe === 'function') {
      return clientSessionViewStore.subscribe(onStoreChange)
    }
    return () => {}
  }, [])

  const locale = useSyncExternalStore(subscribe, readLocaleSnapshot, () => initialLocale)
  const isExplicit = useSyncExternalStore(
    subscribe,
    readExplicitSnapshot,
    () => false
  )

  const { t, i18n } = useTranslation(['chat', 'common', 'marketing', 'report', 'admin'])
  const [, setTick] = useState(0)

  useEffect(() => {
    void i18n.changeLanguage(locale)
    if (typeof document !== 'undefined') {
      document.documentElement.lang = uiLocaleToLanguageCode(locale)
    }
  }, [i18n, locale])

  const setLocale = useCallback((next: UiLocale, options?: { explicit?: boolean }) => {
    clientSessionViewStore.setUiLocale(next, { explicit: options?.explicit ?? true })
    void i18n.changeLanguage(next)
    if (typeof document !== 'undefined') {
      document.documentElement.lang = uiLocaleToLanguageCode(next)
    }
    const sessionId = clientSessionViewStore.getSnapshot().sessionId?.trim()
    if (sessionId) void persistSessionUiLocale({ sessionId, uiLocale: next })
    setTick(n => n + 1)
  }, [i18n])

  const promoteLocaleFromAgent = useCallback((languageCode: string) => {
    const snapshot = clientSessionViewStore.getSnapshot()
    if (isUiLocaleExplicit(snapshot)) return
    const mapped = languageCodeToUiLocale(languageCode)
    if (!mapped) return
    clientSessionViewStore.setUiLocale(mapped, { explicit: false })
    void i18n.changeLanguage(mapped)
    if (typeof document !== 'undefined') {
      document.documentElement.lang = uiLocaleToLanguageCode(mapped)
    }
    setTick(n => n + 1)
  }, [i18n])

  const languageCode = useMemo(() => uiLocaleToLanguageCode(locale), [locale])

  return {
    locale,
    setLocale,
    promoteLocaleFromAgent,
    languageCode,
    t,
    isExplicit,
  }
}
